import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Avatar,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  MenuItem,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssignmentIcon from '@mui/icons-material/Assignment';
import MedicationIcon from '@mui/icons-material/Medication';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import ReportIcon from '@mui/icons-material/Assessment';
import PrintIcon from '@mui/icons-material/Print';
import MessageIcon from '@mui/icons-material/Message';
import InfoIcon from '@mui/icons-material/Info';
import ScienceIcon from '@mui/icons-material/Science';
import SummarizeIcon from '@mui/icons-material/Summarize';
import NotesIcon from '@mui/icons-material/Notes';
import { format } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../firebase/config';
import { useDispatch } from 'react-redux';
import { createPrescription } from '../../redux/slices/prescriptionsSlice';
import { searchMedications } from '../../redux/slices/medicationsSlice';
import CreatePharmacy from '../pharmacy/CreatePharmacy';
import EncounterList from '../encounters/EncounterList';
import PersonalizedReporting from '../reports/PersonalizedReporting';
import MedicalHistoryTab from './MedicalHistoryTab';
import PrescriptionPrintComponent from '../prescriptions/PrescriptionPrintComponent';
import { useNavigate } from 'react-router-dom';
import PatientPortalPanel from './PatientPortalPanel';
import PatientLinkTab from './PatientLinkTab';
import PatientMessagesTab from './PatientMessagesTab';
import PatientOncologyTab from './PatientOncologyTab';
import CaseSummaryTab from './CaseSummaryTab';
import PatientNotesHistoryTab from './PatientNotesHistoryTab';
// Use service layer instead of direct Firestore access
import { getPatientDocuments, createPatientDocument, deletePatientDocument } from '../../services/patientService';
import { calculateAge } from '../../utils/dateUtils';
import DocumentScanButton from '../common/DocumentScanButton';

// Helper to safely convert potentially object values to strings for TextField display
const safeStringValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    // Handle address objects
    if (value.formatted_address) return value.formatted_address;
    if (value.street || value.city) {
      const parts = [value.street, value.city, value.state, value.zip].filter(Boolean);
      return parts.join(', ');
    }
    // Handle insurance objects
    if (value.provider || value.name) return value.provider || value.name;
    // Fallback - don't display [object Object]
    return '';
  }
  return '';
};

// Styled Components - Compact versions
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(1.5),
  borderRadius: 8,
  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 6,
  textTransform: 'none',
  fontWeight: 600,
  padding: '6px 12px',
  fontSize: '0.875rem',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
}));

const CompactSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 6,
  marginBottom: theme.spacing(1),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1rem',
  color: theme.palette.primary.main,
  padding: '8px 12px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.grey[50],
  margin: 0,
}));

const SectionHeaderWithIcon = ({ icon: Icon, children, ...props }) => (
  <SectionHeader {...props} component="div">
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {Icon && <Icon sx={{ fontSize: '1.2rem' }} />}
      {children}
    </Box>
  </SectionHeader>
);

const SectionContent = styled(Box)({
  padding: '12px',
});

const CompactTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    fontSize: '0.875rem',
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
  },
}));

// Document Upload Section Component
const DocumentUploadSection = ({ patient, formData, setFormData, handleViewDocument }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Load existing documents when component mounts
  useEffect(() => {
    const loadExistingDocuments = async () => {
      if (patient?.id) {
        // Load documents for existing patient
        try {
          const documents = await getPatientDocuments(patient.id);
          setUploadedDocuments(documents);
        } catch (error) {
          console.error('Error loading documents:', error);
        }
      } else if (formData?.uploadedDocuments) {
        // Load temporary documents for new patient
        setUploadedDocuments(formData.uploadedDocuments);
      }
    };

    loadExistingDocuments();
  }, [patient?.id, formData?.uploadedDocuments]);

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    setError(null);

    try {
      const uploadPromises = files.map(async (file) => {
        // Validate file size (10MB limit)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
          throw new Error(`File ${file.name} is too large. Maximum size is 10MB.`);
        }

        // Generate unique filename
        const timestamp = new Date().getTime();
        const filename = `${timestamp}-${file.name}`;
        
        // Use existing patient ID or temporary ID for new patients
        const patientId = patient?.id || `temp-${Date.now()}`;
        
        // Create storage reference
        const storageRef = ref(storage, `patient-documents/${patientId}/${filename}`);
        
        // Upload file
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        return {
          name: file.name,
          originalName: file.name,
          url: downloadURL,
          size: file.size,
          type: file.type,
          uploadedAt: new Date(),
          patientId: patientId,
          storagePath: `patient-documents/${patientId}/${filename}`,
          isTemporary: !patient?.id // Flag for temporary uploads
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      
      // For existing patients, store in Firestore immediately
      // For new patients, we'll store these temporarily and save them when patient is created
      if (patient?.id) {
        const documentPromises = uploadedFiles.map(async (fileData) => {
          const document = await createPatientDocument({
            ...fileData,
            uploadedAt: new Date(),
          });
          return { id: document.id, ...fileData };
        });

        const documentsWithIds = await Promise.all(documentPromises);
        setUploadedDocuments(prev => [...prev, ...documentsWithIds]);
      } else {
        // For new patients, just add to local state with temporary IDs
        const tempDocuments = uploadedFiles.map((fileData, index) => ({
          id: `temp-${Date.now()}-${index}`,
          ...fileData
        }));
        setUploadedDocuments(prev => [...prev, ...tempDocuments]);
        
        // Also update formData to include these documents
        if (setFormData) {
          setFormData(prev => ({
            ...prev,
            uploadedDocuments: [...(prev.uploadedDocuments || []), ...tempDocuments]
          }));
        }
      }
      
      setSuccess(`Successfully uploaded ${uploadedFiles.length} document(s).`);
    } catch (error) {
      console.error('Error uploading files:', error);
      setError(`Failed to upload documents: ${error.message}`);
    } finally {
      setIsUploading(false);
      // Clear the input
      event.target.value = '';
    }
  };

  const handleUploadClick = () => {
    document.getElementById('patient-document-upload-input').click();
  };

  const handleRemoveDocument = async (documentId, storagePath, isTemporary = false) => {
    try {
      // Only delete from Firestore if it's not a temporary document
      if (!isTemporary && !documentId.startsWith('temp-')) {
        await deletePatientDocument(documentId);
      }
      
      // Delete from Storage if path exists
      if (storagePath) {
        const storageRef = ref(storage, storagePath);
        await deleteObject(storageRef);
      }

      // Remove from local state
      setUploadedDocuments(prev => prev.filter(doc => doc.id !== documentId));
      
      // Also remove from formData if it exists
      if (setFormData) {
        setFormData(prev => ({
          ...prev,
          uploadedDocuments: (prev.uploadedDocuments || []).filter(doc => doc.id !== documentId)
        }));
      }
      
      setSuccess('Document deleted successfully.');
    } catch (error) {
      console.error('Error deleting document:', error);
      setError('Failed to delete document.');
    }
  };

  return (
    <Box>
      {/* Upload Section */}
      <Box sx={{ mb: 3 }}>
        <Button 
          variant="outlined" 
          onClick={handleUploadClick}
          disabled={isUploading}
          startIcon={isUploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
          sx={{ mb: 2 }}
        >
          {isUploading ? 'Uploading...' : 'Upload Documents'}
        </Button>
        <input
          id="patient-document-upload-input"
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
        />
        
        {isUploading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Uploading documents...
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
      </Box>

      {/* Documents List */}
      {uploadedDocuments.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Documents ({uploadedDocuments.length})
          </Typography>
          <List>
            {uploadedDocuments.map((doc) => (
              <ListItem
                key={doc.id}
                sx={{ 
                  py: 1, 
                  px: 2, 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  backgroundColor: 'background.paper'
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <AttachFileIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={doc.name}
                  secondary={
                    <Box>
                      <Typography variant="caption" display="block">
                        {(doc.size / 1024).toFixed(1)} KB • {doc.type}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Uploaded: {doc.uploadedAt && format(new Date(doc.uploadedAt.seconds ? doc.uploadedAt.toDate() : doc.uploadedAt), 'MMM dd, yyyy HH:mm')}
                      </Typography>
                    </Box>
                  }
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton 
                    size="small" 
                    onClick={() => handleViewDocument(doc.url)}
                    color="primary"
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleRemoveDocument(doc.id, doc.storagePath, doc.isTemporary)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {uploadedDocuments.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4, fontStyle: 'italic' }}>
          No documents uploaded yet
        </Typography>
      )}
    </Box>
  );
};

// Document Display Section Component (Read-only view)
const DocumentDisplaySection = ({ patient, handleViewDocument }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDocuments = async () => {
      if (!patient?.id) {
        setLoading(false);
        return;
      }
      
      try {
        const documents = await getPatientDocuments(patient.id);
        setDocuments(documents);
      } catch (error) {
        console.error('Error loading documents:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDocuments();
  }, [patient?.id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {documents.length > 0 ? (
        <Box>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Documents ({documents.length})
          </Typography>
          <List>
            {documents.map((doc) => (
              <ListItem
                key={doc.id}
                sx={{ 
                  py: 1, 
                  px: 2, 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  backgroundColor: 'background.paper'
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <AttachFileIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={doc.name}
                  secondary={
                    <Box>
                      <Typography variant="caption" display="block">
                        {(doc.size / 1024).toFixed(1)} KB • {doc.type}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Uploaded: {doc.uploadedAt && format(new Date(doc.uploadedAt.seconds ? doc.uploadedAt.toDate() : doc.uploadedAt), 'MMM dd, yyyy HH:mm')}
                      </Typography>
                    </Box>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
                <IconButton 
                  size="small" 
                  onClick={() => handleViewDocument(doc.url)}
                  color="primary"
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </ListItem>
            ))}
          </List>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4, fontStyle: 'italic' }}>
          No documents uploaded for this patient
        </Typography>
      )}
    </Box>
  );
};

// Prescription Management Section Component (Edit Mode)
const PrescriptionManagementSection = ({ patient, formData, setFormData, prescriptions = [] }) => {
  const dispatch = useDispatch();
  const [medications, setMedications] = useState([{
    name: '',
    dosage: '',
    route: '',
    frequency: '',
    duration: ''
  }]);
  const [pharmacy, setPharmacy] = useState({ name: '', address: '', phone: '' });
  const [showPharmacyDialog, setShowPharmacyDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const routeOptions = [
    'Oral',
    'Intravenous',
    'Intramuscular',
    'Subcutaneous',
    'Topical',
    'Inhalation'
  ];

  const handleAddMedication = () => {
    setMedications([...medications, {
      name: '',
      dosage: '',
      route: '',
      frequency: '',
      duration: ''
    }]);
  };

  const handleRemoveMedication = (index) => {
    const newMedications = medications.filter((_, i) => i !== index);
    setMedications(newMedications);
  };

  const handleMedicationChange = async (index, field, value) => {
    const newMedications = [...medications];
    newMedications[index] = {
      ...newMedications[index],
      [field]: value
    };
    setMedications(newMedications);

    // Search for medications when name is being typed
    if (field === 'name' && value.length > 2) {
      // setIsSearching(true); // This line was removed
      try {
        const result = await dispatch(searchMedications(value));
        if (result.payload) {
          // setMedicationSearchOptions(result.payload); // This line was removed
        }
      } catch (error) {
        console.error('Error searching medications:', error);
      } finally {
        // setIsSearching(false); // This line was removed
      }
    }
  };

  const handlePharmacySelect = (selectedPharmacy) => {
    if (selectedPharmacy) {
      setPharmacy({
        name: selectedPharmacy.name || '',
        address: selectedPharmacy.address || '',
        phone: selectedPharmacy.phone || '',
        id: selectedPharmacy.id || null
      });
    }
  };

  const handleSubmitPrescription = async () => {
    if (!patient?.id) {
      setError('Patient ID is required to create prescriptions.');
      return;
    }

    // Validate medications
    const validMedications = medications.filter(med => 
      med.name && med.dosage && med.route && med.frequency && med.duration
    );

    if (validMedications.length === 0) {
      setError('Please fill in at least one complete medication.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await dispatch(createPrescription({
        patientId: patient.id,
        medications: validMedications,
        pharmacy: pharmacy.name ? pharmacy : null
      }));

      setSuccess('Prescription created successfully!');
      
      // Reset form
      setMedications([{
        name: '',
        dosage: '',
        route: '',
        frequency: '',
        duration: ''
      }]);
      setPharmacy({ name: '', address: '', phone: '' });
      
    } catch (error) {
      console.error('Error creating prescription:', error);
      setError('Failed to create prescription. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      {/* Error and Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Pharmacy Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Pharmacy Information
        </Typography>
        {pharmacy.name ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body2">
              {pharmacy.name} - {pharmacy.address}
            </Typography>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => setShowPharmacyDialog(true)}
            >
              Update Pharmacy
            </Button>
          </Box>
        ) : (
          <Button
            variant="outlined"
            onClick={() => setShowPharmacyDialog(true)}
            sx={{ mb: 2 }}
          >
            Add Pharmacy
          </Button>
        )}
      </Box>

      {/* Medications Section */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Medications
      </Typography>
      
      {medications.map((medication, index) => (
        <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Medication {index + 1}
            </Typography>
            {medications.length > 1 && (
              <IconButton onClick={() => handleRemoveMedication(index)} color="error" size="small">
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <CompactTextField
                label="Medication Name"
                value={medication.name}
                onChange={(e) => handleMedicationChange(index, 'name', e.target.value)}
                fullWidth
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CompactTextField
                label="Dosage"
                value={medication.dosage}
                onChange={(e) => handleMedicationChange(index, 'dosage', e.target.value)}
                fullWidth
                size="small"
                placeholder="e.g., 500mg"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Route</InputLabel>
                <Select
                  value={medication.route}
                  label="Route"
                  onChange={(e) => handleMedicationChange(index, 'route', e.target.value)}
                >
                  {routeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <CompactTextField
                label="Frequency"
                value={medication.frequency}
                onChange={(e) => handleMedicationChange(index, 'frequency', e.target.value)}
                fullWidth
                size="small"
                placeholder="e.g., Twice daily"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <CompactTextField
                label="Duration"
                value={medication.duration}
                onChange={(e) => handleMedicationChange(index, 'duration', e.target.value)}
                fullWidth
                size="small"
                placeholder="e.g., 7 days"
                required
              />
            </Grid>
          </Grid>
        </Box>
      ))}

      {/* Add Medication Button */}
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddMedication}
        sx={{ mb: 3 }}
      >
        Add Another Medication
      </Button>

      {/* Submit Button */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleSubmitPrescription}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          {isSubmitting ? 'Creating...' : 'Create Prescription'}
        </Button>
      </Box>

      {/* Pharmacy Dialog */}
      <Dialog open={showPharmacyDialog} onClose={() => setShowPharmacyDialog(false)} maxWidth="md" fullWidth>
        <CreatePharmacy 
          onClose={(savedPharmacy) => {
            handlePharmacySelect(savedPharmacy);
            setShowPharmacyDialog(false);
          }}
          pharmacy={pharmacy.name ? pharmacy : null}
        />
      </Dialog>
    </Box>
  );
};

// Prescription Display Section Component (Read-only view)
const PrescriptionDisplaySection = ({ patient, prescriptions = [], facilityId = '' }) => {
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const activePrescriptions = prescriptions.filter(p => p.status === 'active');

  return (
    <Box>
      {activePrescriptions.length > 0 ? (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Active Prescriptions ({activePrescriptions.length})
            </Typography>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => setPrintDialogOpen(true)}
              size="small"
              sx={{ 
                textTransform: 'none',
                fontWeight: 600,
                borderColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white'
                }
              }}
            >
              Print Prescriptions
            </Button>
          </Box>
          <List>
            {activePrescriptions.map((prescription, index) => (
              <ListItem
                key={prescription.id || index}
                sx={{ 
                  py: 2, 
                  px: 2, 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 2,
                  backgroundColor: 'background.paper'
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <MedicationIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Prescription from {prescription.createdAt ? format(new Date(prescription.createdAt), 'MMM dd, yyyy') : 'Unknown date'}
                      </Typography>
                      {prescription.medications && prescription.medications.map((med, idx) => (
                        <Box key={idx} sx={{ mb: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {med.name}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Dosage: {med.dosage} • Frequency: {med.frequency}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Duration: {med.duration} • Route: {med.route}
                          </Typography>
                        </Box>
                      ))}
                      {prescription.pharmacy && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Pharmacy: {prescription.pharmacy.name}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4, fontStyle: 'italic' }}>
          No active prescriptions for this patient
        </Typography>
      )}

      {/* Print Dialog */}
      <PrescriptionPrintComponent
        prescriptions={prescriptions}
        patient={patient}
        encounter={null}
        facilityId={facilityId}
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        onPrint={() => setPrintDialogOpen(false)}
      />
    </Box>
  );
};

// Base tab keys - patientLinks will be conditionally added for non-minors
const BASE_TAB_KEYS = ['demographics', 'caseSummary', 'history', 'encounters', 'notesHistory', 'prescriptions', 'reports', 'documents', 'messages', 'oncology'];
const PatientBase = ({
  mode = 'view', // 'view', 'edit', 'create'
  patient = null,
  encounters = [],
  prescriptions = [],
  orders = [],
  formData,
  setFormData,
  onSave,
  onBack,
  onEdit,
  onPatientPortalToggle,
  role = '',
  loading = false,
  saving = false,
  title = 'Patient Details',
  subtitle = null,
  showActions = true,
  facilityId = '',
  isMinor = false, // New prop to indicate if patient is a minor
  currentUserId = '', // Current logged-in user ID
  currentUserName = '', // Current logged-in user name
}) => {
    // Determine if patient is a minor (either from prop or calculated from existing patient data)
  const isPatientMinor = isMinor || (patient?.dateOfBirth && calculateAge(patient.dateOfBirth) <= 18);
  
  // Determine available tabs based on patient status - memoized to prevent unnecessary re-renders
  const availableTabs = useMemo(() => {
    let tabs = BASE_TAB_KEYS;
    
    // Add patientLinks tab for non-minors
    if (!isPatientMinor) {
      tabs = [...tabs, 'patientLinks'];
    }
    
    return tabs;
  }, [isPatientMinor]);
  const [mainTab, setMainTab] = useState(availableTabs[0]);
  const [editMode, setEditMode] = useState(mode === 'create' || mode === 'edit');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const navigate = useNavigate();

  // Handle driver's license scan data
  const handleLicenseScanData = (data) => {
    if (!data || !setFormData) return;
    
    // Build full address from components
    const addressParts = [
      data.address,
      data.city,
      data.state,
      data.zipCode
    ].filter(Boolean);
    const fullAddress = addressParts.join(', ');
    
    // Map sex to gender format
    let gender = '';
    if (data.sex === 'M') gender = 'Male';
    else if (data.sex === 'F') gender = 'Female';
    else if (data.sex) gender = 'Other';
    
    setFormData(prev => ({
      ...prev,
      firstName: data.firstName || prev.firstName,
      lastName: data.lastName || prev.lastName,
      dateOfBirth: data.dateOfBirth || prev.dateOfBirth,
      gender: gender || prev.gender,
      address: fullAddress || prev.address,
    }));
    
    setSnackbar({
      open: true,
      message: 'Driver\'s license data extracted successfully!',
      severity: 'success'
    });
  };

  // Handle insurance card scan data
  const handleInsuranceScanData = (data) => {
    if (!data || !setFormData) return;
    
    setFormData(prev => ({
      ...prev,
      insurance: data.insurerName || prev.insurance,
      policyNumber: data.memberId || prev.policyNumber,
    }));
    
    setSnackbar({
      open: true,
      message: 'Insurance card data extracted successfully!',
      severity: 'success'
    });
  };

  // Check if patient portal is enabled and patient is 18+
  const isPatientPortalEnabled = patient?.isPatientPortalEnabled === true;
  const patientAge = patient?.dateOfBirth ? calculateAge(patient.dateOfBirth) : null;
  const isPatientAdult = patientAge && patientAge >= 18;
  const canAccessMessages = isPatientPortalEnabled && isPatientAdult;

 

  // If patient is a minor and current tab is patientLinks, redirect to demographics
  useEffect(() => {
    if (isPatientMinor && mainTab === 'patientLinks') {
      setMainTab('demographics');
    }
  }, [isPatientMinor, mainTab]);

  // Update mainTab if current tab becomes unavailable
  useEffect(() => {
    if (!availableTabs.includes(mainTab)) {
      setMainTab(availableTabs[0]);
    }
  }, [availableTabs, mainTab]);

  // Calculate patient age


  const formatDateOfBirth = (dateOfBirth) => {
    if (!dateOfBirth) return 'Not specified';
    try {
      const date = new Date(dateOfBirth);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  // Get patient initials for avatar
  const getInitials = (name, firstName, lastName) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (!name) return 'P';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Get status color based on patient status
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'deceased': return 'error';
      case 'inpatient': return 'warning';
      default: return 'primary';
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave();
    }
    setEditMode(false);
  };

  const handleCancel = () => {
    setEditMode(false);
    if (mode === 'create' && onBack) {
      onBack();
    }
  };

  const handleEdit = () => {
    setEditMode(true);
    if (onEdit) {
      onEdit();
    }
  };

  return (
    <Box sx={{ maxWidth: '1400px', margin: '0 auto', p: 2 }}>
      {/* Header Card */}
      <HeaderCard>
        <CardContent sx={{ pb: '16px !important' }}>
          <Grid container spacing={2} alignItems="center">
            {/* Patient Avatar and Basic Info */}
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white'
                  }}
                >
                  {patient ? getInitials(patient.name, patient.firstName, patient.lastName) : 'NP'}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {title}
                  </Typography>
                  {subtitle && (
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      {subtitle}
                    </Typography>
                  )}
                  {isPatientMinor && (
                    <Chip
                      label="Minor Patient (18 or younger)"
                      color="warning"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    {(patient?.firstName || patient?.lastName || patient?.name) && (
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {patient?.firstName && patient?.lastName 
                          ? `${patient.firstName} ${patient.lastName}` 
                          : patient?.name || 'Unknown Patient'}
                      </Typography>
                    )}
                    {patient?.mrn && (
                      <>
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>|</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          {patient.mrn.startsWith('MRN') ? patient.mrn : `MRN: ${patient.mrn}`}
                        </Typography>
                      </>
                    )}
                    {patient?.dateOfBirth && (
                      <>
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>|</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          DOB: {formatDateOfBirth(patient.dateOfBirth)}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>|</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600, color: 'primary.main' }}>
                          Age: {calculateAge(patient.dateOfBirth)} years
                        </Typography>
                      </>
                    )}
                    {patient?.status && (
                      <Chip
                        label={patient.status}
                        size="small"
                        color={getStatusColor(patient.status)}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Action Buttons */}
            {showActions && (
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  {editMode ? (
                    <>
                      <ActionButton
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                        color="primary"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </ActionButton>
                      <ActionButton
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleCancel}
                        disabled={saving}
                        sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
                      >
                        Cancel
                      </ActionButton>
                    </>
                  ) : (
                    <>
                      <ActionButton
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={handleEdit}
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                      >
                        Edit
                      </ActionButton>
                      {patient && (
                        <ActionButton
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => navigate(`/patients/${patient.id}/encounters/new`)}
                          sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
                        >
                          New Encounter
                        </ActionButton>
                      )}
                      {onBack && (
                        <ActionButton
                          variant="outlined"
                          onClick={onBack}
                          sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
                        >
                          Back
                        </ActionButton>
                      )}
                    </>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </HeaderCard>

      {/* Main Tabs */}
      <Box sx={{ 
        background: '#ffffff',
        borderRadius: 2,
        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
        mb: 1
      }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={mainTab} 
            onChange={(_, v) => setMainTab(v)} 
            variant="scrollable"
            scrollButtons="auto"
            sx={{ '& .MuiTab-root': { minHeight: 42, textTransform: 'none', fontWeight: 600 } }}
          >
            <Tab label="Demographics" value="demographics" />
            <Tab label="Case Summary" value="caseSummary" />
            <Tab label="Medical History" value="history" />
            <Tab label="Encounters" value="encounters" />
            <Tab label="Notes History" value="notesHistory" />
            <Tab label="Prescriptions" value="prescriptions" />
            <Tab label="Reports" value="reports" />
            <Tab label="Documents" value="documents" />
            <Tab 
              label="Messages" 
              value="messages" 
              disabled={!canAccessMessages}
              onClick={() => {
                if (!canAccessMessages) {
                  setSnackbar({
                    open: true,
                    message: 'Enable Patient Portal to access this feature. Patient must be 18 years or older.',
                    severity: 'warning'
                  });
                }
              }}
            />
            {!isPatientMinor && <Tab label="Patient Links" value="patientLinks" />}
            <Tab label="Oncology" value="oncology" />
          </Tabs>
        </Box>

        <Box sx={{ p: 2 }}>
          {/* Demographics Tab */}
          {mainTab === 'demographics' && (
            <Grid container spacing={2}>
              {/* Left Column - Basic Information */}
              <Grid item xs={12} lg={6}>
                <CompactSection>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                    <SectionHeaderWithIcon icon={PersonIcon}>Basic Information</SectionHeaderWithIcon>
                    {editMode && (
                      <DocumentScanButton
                        documentType="drivers_license"
                        onDataExtracted={handleLicenseScanData}
                        buttonLabel="Scan License"
                        buttonSize="small"
                      />
                    )}
                  </Box>
                  <SectionContent>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={6}>
                        <CompactTextField
                          label="First Name"
                          value={editMode ? (formData?.firstName || '') : (patient?.firstName || '')}
                          onChange={e => editMode && setFormData && setFormData(f => ({ ...f, firstName: e.target.value }))}
                          fullWidth
                          size="small"
                          disabled={!editMode}
                          required
                          error={editMode && !formData?.firstName && formData?.firstName !== ''}
                          helperText={editMode && !formData?.firstName && formData?.firstName !== '' ? 'First name is required' : ''}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <CompactTextField
                          label="Last Name"
                          value={editMode ? (formData?.lastName || '') : (patient?.lastName || '')}
                          onChange={e => editMode && setFormData && setFormData(f => ({ ...f, lastName: e.target.value }))}
                          fullWidth
                          size="small"
                          disabled={!editMode}
                          required
                          error={editMode && !formData?.lastName && formData?.lastName !== ''}
                          helperText={editMode && !formData?.lastName && formData?.lastName !== '' ? 'Last name is required' : ''}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label="Date of Birth"
                          value={(editMode ? formData?.dateOfBirth : patient?.dateOfBirth) ? new Date(editMode ? formData?.dateOfBirth : patient?.dateOfBirth) : null}
                          onChange={(date) => editMode && setFormData && setFormData(f => ({ ...f, dateOfBirth: date ? date.toISOString().split('T')[0] : '' }))}
                          disabled={!editMode}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: 'small',
                              required: true,
                              error: editMode && !formData?.dateOfBirth,
                              helperText: (editMode ? formData?.dateOfBirth : patient?.dateOfBirth) ? 
                                `Age: ${calculateAge(editMode ? formData?.dateOfBirth : patient?.dateOfBirth)} years${isPatientMinor ? ' (Minor - Patient Portal and Links disabled)' : ''}` : 
                                'Date of birth is required'
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <CompactTextField
                          label="Gender"
                          select
                          value={(() => {
                            const rawGender = editMode ? formData?.gender : patient?.gender;
                            if (!rawGender) return '';
                            // Normalize to match menu options
                            const normalized = rawGender.charAt(0).toUpperCase() + rawGender.slice(1).toLowerCase();
                            // Check if it matches one of the valid options
                            const validOptions = ['Male', 'Female', 'Other', 'Prefer not to say'];
                            return validOptions.includes(normalized) ? normalized : '';
                          })()}
                          onChange={e => editMode && setFormData && setFormData(f => ({ ...f, gender: e.target.value }))}
                          fullWidth
                          size="small"
                          disabled={!editMode}
                        >
                          <MenuItem value="">Select Gender</MenuItem>
                          <MenuItem value="Male">Male</MenuItem>
                          <MenuItem value="Female">Female</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                          <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
                        </CompactTextField>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <CompactTextField
                          label="Blood Type"
                          select
                          value={editMode ? (formData?.bloodType || '') : (patient?.bloodType || '')}
                          onChange={e => editMode && setFormData && setFormData(f => ({ ...f, bloodType: e.target.value }))}
                          fullWidth
                          size="small"
                          disabled={!editMode}
                        >
                          <MenuItem value="">Select Blood Type</MenuItem>
                          <MenuItem value="A+">A+</MenuItem>
                          <MenuItem value="A-">A-</MenuItem>
                          <MenuItem value="B+">B+</MenuItem>
                          <MenuItem value="B-">B-</MenuItem>
                          <MenuItem value="AB+">AB+</MenuItem>
                          <MenuItem value="AB-">AB-</MenuItem>
                          <MenuItem value="O+">O+</MenuItem>
                          <MenuItem value="O-">O-</MenuItem>
                        </CompactTextField>
                      </Grid>
                    </Grid>
                  </SectionContent>
                </CompactSection>
              </Grid>

              {/* Right Column - Contact Information */}
              <Grid item xs={12} lg={6}>
                <CompactSection>
                  <SectionHeaderWithIcon icon={ContactPhoneIcon}>Contact Information</SectionHeaderWithIcon>
                  <SectionContent>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12}>
                        <CompactTextField
                          label="Email Address"
                          type="email"
                          value={editMode ? (formData?.email || '') : (patient?.email || '')}
                          onChange={e => editMode && setFormData && setFormData(f => ({ ...f, email: e.target.value }))}
                          fullWidth
                          size="small"
                          disabled={!editMode}
                          required={!isPatientMinor && (!formData?.contact || formData?.contact === '')}
                          error={editMode && !isPatientMinor && !formData?.email && !formData?.contact && formData?.email !== '' && formData?.contact !== ''}
                          helperText={
                            isPatientMinor 
                              ? 'Email is optional for patients under 18' 
                              : (editMode && !formData?.email && !formData?.contact && formData?.email !== '' && formData?.contact !== '' ? 'Either email or phone number is required' : '')
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <CompactTextField
                          label="Phone Number"
                          value={editMode ? (formData?.contact || '') : (patient?.contact || '')}
                          onChange={e => editMode && setFormData && setFormData(f => ({ ...f, contact: e.target.value }))}
                          fullWidth
                          size="small"
                          disabled={!editMode}
                          required={isPatientMinor || !formData?.email || formData?.email === ''}
                          error={editMode && (isPatientMinor ? !formData?.contact : (!formData?.email && !formData?.contact && formData?.email !== '' && formData?.contact !== ''))}
                          helperText={
                            isPatientMinor 
                              ? (editMode && !formData?.contact ? 'Phone number is required for patients under 18' : '')
                              : (editMode && !formData?.email && !formData?.contact && formData?.email !== '' && formData?.contact !== '' ? 'Either email or phone number is required' : '')
                          }
                        />
                      </Grid>
                      {/* Emergency Contact Fields */}
                      <Grid item xs={12} md={6}>
                        <CompactTextField
                          label="Emergency Contact Name"
                          value={editMode ? 
                            (formData?.emergencyContact?.name || '') : 
                            (patient?.emergencyContact?.name || '')
                          }
                          onChange={e => editMode && setFormData && setFormData(f => ({ 
                            ...f, 
                            emergencyContact: {
                              ...(f.emergencyContact || {}),
                              name: e.target.value
                            }
                          }))}
                          fullWidth
                          size="small"
                          disabled={!editMode}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <CompactTextField
                          label="Emergency Contact Relationship"
                          value={editMode ? 
                            (formData?.emergencyContact?.relationship || '') : 
                            (patient?.emergencyContact?.relationship || '')
                          }
                          onChange={e => editMode && setFormData && setFormData(f => ({ 
                            ...f, 
                            emergencyContact: {
                              ...(f.emergencyContact || {}),
                              relationship: e.target.value
                            }
                          }))}
                          fullWidth
                          size="small"
                          disabled={!editMode}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <CompactTextField
                          label="Emergency Contact Phone"
                          value={editMode ? 
                            (formData?.emergencyContact?.phone || '') : 
                            (patient?.emergencyContact?.phone || '')
                          }
                          onChange={e => editMode && setFormData && setFormData(f => ({ 
                            ...f, 
                            emergencyContact: {
                              ...(f.emergencyContact || {}),
                              phone: e.target.value
                            }
                          }))}
                          fullWidth
                          size="small"
                          disabled={!editMode}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CompactTextField
                          label="Address"
                          value={editMode ? safeStringValue(formData?.address) : safeStringValue(patient?.address)}
                          onChange={e => editMode && setFormData && setFormData(f => ({ ...f, address: e.target.value }))}
                          fullWidth
                          size="small"
                          disabled={!editMode}
                          inputRef={(input) => {
                            if (input && editMode && window.google && window.google.maps) {
                              const autocomplete = new window.google.maps.places.Autocomplete(input, {
                                types: ['address'],
                                componentRestrictions: { country: 'us' }
                              });
                              autocomplete.addListener('place_changed', () => {
                                const place = autocomplete.getPlace();
                                if (place.formatted_address) {
                                  setFormData(f => ({ ...f, address: place.formatted_address }));
                                }
                              });
                            }
                          }}
                          placeholder="Start typing to get address suggestions..."
                        />
                      </Grid>
                    </Grid>
                  </SectionContent>
                </CompactSection>
              </Grid>

              {/* Allergies Panel */}
              <Grid item xs={12} lg={6}>
                <CompactSection>
                  <SectionHeaderWithIcon icon={WarningIcon}>Allergies</SectionHeaderWithIcon>
                  <SectionContent>
                    <CompactTextField
                      label="Known Allergies"
                      value={editMode ? safeStringValue(formData?.allergies) : (patient?.allergies ? 
                        (Array.isArray(patient.allergies) ? patient.allergies.join(', ') : safeStringValue(patient.allergies)) : ''
                      )}
                      onChange={e => editMode && setFormData && setFormData(f => ({ ...f, allergies: e.target.value }))}
                      fullWidth
                      size="small"
                      disabled={!editMode}
                      multiline
                      rows={3}
                      placeholder="Enter allergies separated by commas (e.g., Penicillin, Peanuts, Latex)"
                      helperText="List known allergies separated by commas. Leave blank if no known allergies."
                    />
                  </SectionContent>
                </CompactSection>
              </Grid>

              {/* Insurance Information Panel */}
              <Grid item xs={12} lg={6}>
                <CompactSection>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                    <SectionHeaderWithIcon icon={LocalHospitalIcon}>Insurance Information</SectionHeaderWithIcon>
                    {editMode && (
                      <DocumentScanButton
                        documentType="insurance_card"
                        onDataExtracted={handleInsuranceScanData}
                        buttonLabel="Scan Insurance"
                        buttonSize="small"
                      />
                    )}
                  </Box>
                  <SectionContent>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12}>
                        <CompactTextField
                          label="Insurance Provider"
                          value={editMode ? safeStringValue(formData?.insurance) : safeStringValue(patient?.insurance)}
                          onChange={e => editMode && setFormData && setFormData(f => ({ ...f, insurance: e.target.value }))}
                          fullWidth
                          size="small"
                          disabled={!editMode}
                          placeholder="e.g., Blue Cross Blue Shield, Aetna, Cigna"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CompactTextField
                          label="Policy Number"
                          value={editMode ? (formData?.policyNumber || '') : (patient?.policyNumber || '')}
                          onChange={e => editMode && setFormData && setFormData(f => ({ ...f, policyNumber: e.target.value }))}
                          fullWidth
                          size="small"
                          disabled={!editMode}
                          placeholder="Insurance policy number"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CompactTextField
                          label="Primary Language"
                          value={editMode ? (formData?.primaryLanguage || '') : (patient?.primaryLanguage || '')}
                          onChange={e => editMode && setFormData && setFormData(f => ({ ...f, primaryLanguage: e.target.value }))}
                          fullWidth
                          size="small"
                          disabled={!editMode}
                          placeholder="e.g., English, Spanish, French"
                        />
                      </Grid>
                    </Grid>
                  </SectionContent>
                </CompactSection>
              </Grid>

              {/* Patient Portal Access Panel - Only show for non-minors */}
              {!isPatientMinor && (
                <Grid item xs={12} lg={6}>
                  <PatientPortalPanel
                    patient={patient}
                    role={role}
                    onUpdate={(updatedPatient) => {
                      // Update the patient data in the parent component
                      if (onPatientPortalToggle) {
                        onPatientPortalToggle(updatedPatient.id, updatedPatient.isPatientPortalEnabled);
                      }
                    }}
                  />
                </Grid>
              )}
            </Grid>
          )}

          {/* Case Summary Tab */}
          {mainTab === 'caseSummary' && (
            <CaseSummaryTab
              patient={patient}
              facilityId={facilityId}
            />
          )}

          {/* Medical History Tab */}
          {mainTab === 'history' && (
            <CompactSection>
              <SectionHeaderWithIcon icon={LocalHospitalIcon}>Medical History</SectionHeaderWithIcon>
              <SectionContent>
                <MedicalHistoryTab patient={patient} editMode={editMode} />
              </SectionContent>
            </CompactSection>
          )}

          {/* Encounters Tab */}
          {mainTab === 'encounters' && (
            <CompactSection>
              <SectionHeaderWithIcon icon={AssignmentIcon}>Encounters</SectionHeaderWithIcon>
              <SectionContent>
                <EncounterList patientId={patient?.id} embedded={true} />
              </SectionContent>
            </CompactSection>
          )}

          {/* Notes History Tab */}
          {mainTab === 'notesHistory' && (
            <CompactSection>
              <SectionHeaderWithIcon icon={NotesIcon}>Notes History</SectionHeaderWithIcon>
              <SectionContent>
                <PatientNotesHistoryTab 
                  patientId={patient?.id}
                  facilityId={facilityId || patient?.facilityId}
                />
              </SectionContent>
            </CompactSection>
          )}

          {/* Prescriptions Tab */}
          {mainTab === 'prescriptions' && (
            <CompactSection>
              <SectionHeaderWithIcon icon={MedicationIcon}>Prescriptions</SectionHeaderWithIcon>
              <SectionContent>
                {editMode ? (
                  <PrescriptionManagementSection 
                    patient={patient}
                    formData={formData}
                    setFormData={setFormData}
                    prescriptions={prescriptions}
                  />
                ) : (
                  <PrescriptionDisplaySection patient={patient} prescriptions={prescriptions} facilityId={facilityId} />
                )}
              </SectionContent>
            </CompactSection>
          )}

          {/* Reports Tab */}
          {mainTab === 'reports' && (
            <CompactSection>
              <SectionHeaderWithIcon icon={ReportIcon}>Reports</SectionHeaderWithIcon>
              <SectionContent>
                <PersonalizedReporting 
                  facilityId={facilityId || patient?.facilityId || ''}
                  patientId={patient?.id}
                  patient={patient}
                  showPatientHeader={false}
                />
              </SectionContent>
            </CompactSection>
          )}

          {/* Documents Tab */}
          {mainTab === 'documents' && (
            <CompactSection>
              <SectionHeaderWithIcon icon={DocumentScannerIcon}>Documents</SectionHeaderWithIcon>
              <SectionContent>
                {editMode ? (
                  <DocumentUploadSection 
                    patient={patient}
                    formData={formData}
                    setFormData={setFormData}
                    handleViewDocument={url => {
                      if (url.startsWith('http')) {
                        window.open(url, '_blank');
                      } else {
                        navigate(url);
                      }
                    }}
                  />
                ) : (
                  <DocumentDisplaySection patient={patient} handleViewDocument={url => {
                    if (url.startsWith('http')) {
                      window.open(url, '_blank');
                    } else {
                      navigate(url);
                    }
                  }} />
                )}
              </SectionContent>
            </CompactSection>
          )}

          {/* Messages Tab */}
          {mainTab === 'messages' && (
            <CompactSection>
              <SectionHeaderWithIcon icon={MessageIcon}>Messages</SectionHeaderWithIcon>
              <SectionContent>
                {canAccessMessages ? (
                  <PatientMessagesTab 
                    patient={patient}
                    currentUserId={currentUserId}
                    currentUserName={currentUserName}
                  />
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <WarningIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                    <Typography variant="h6" color="warning.main" gutterBottom>
                      Messages Feature Unavailable
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      {!isPatientPortalEnabled 
                        ? 'Patient portal is not enabled for this account.'
                        : 'Patient must be 18 years or older to access messaging.'
                      }
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<InfoIcon />}
                      onClick={() => setSnackbar({
                        open: true,
                        message: 'Enable Patient Portal to access this feature. Patient must be 18 years or older.',
                        severity: 'warning'
                      })}
                      sx={{ mt: 2 }}
                    >
                      Learn More
                    </Button>
                  </Box>
                )}
              </SectionContent>
            </CompactSection>
          )}

          {/* Patient Links Tab */}
          {!isPatientMinor && mainTab === 'patientLinks' && (
            <CompactSection>
              <SectionHeaderWithIcon icon={PersonIcon}>Patient Links</SectionHeaderWithIcon>
              <SectionContent>
                <PatientLinkTab patient={patient} editMode={editMode} />
              </SectionContent>
            </CompactSection>
          )}

          {/* Oncology Tab */}
          {mainTab === 'oncology' && (
            <CompactSection>
              <SectionHeaderWithIcon icon={ScienceIcon}>Oncology</SectionHeaderWithIcon>
              <SectionContent>
                <PatientOncologyTab patient={patient} />
              </SectionContent>
            </CompactSection>
          )}
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PatientBase; 